import React, { PropsWithChildren, useMemo } from "react";

import {
  Box,
  FormHelperText,
  InputBase,
  InputLabel,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { DateDisplay, InputLabelHeight } from "@covertree/types";

import { DateConsumer, DateProvider } from "./DateContext";
import { getInputLabelHeight } from "@covertree/utils";

export interface DateFieldProps {
  id: string;
  name: string;
  children: React.ReactNode;
  isTouched: boolean;
  label?: React.ReactNode;
  labelHeight?: InputLabelHeight;
  error?: string;
  value?: string;
  display?: DateDisplay;
  dataTestId?: string;
  fontSize?: number;
  labelFontSize?: number;
  onChange?(value: string): void;
}

export function DateField({
  id,
  name,
  label,
  labelHeight = InputLabelHeight.DEFAULT,
  error,
  dataTestId,
  fontSize,
  labelFontSize,
  children,
  ...props
}: PropsWithChildren<DateFieldProps>) {
  const labelFontSizeValue = labelFontSize ?? fontSize;
  const theme = useTheme();

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const labelComponentHeight = useMemo(
    () => getInputLabelHeight(theme, labelHeight),
    [labelHeight, theme]
  );
  return (
    <DateProvider {...props}>
      {label && (
        <InputLabel
          htmlFor={id}
          error={!!error}
          sx={() => ({
            height: isDesktop ? labelComponentHeight : "auto",
            display: "flex",
            alignItems: "flex-end",
            ...(labelFontSizeValue && { fontSize: `${labelFontSizeValue}px` }),
            "& > p": {
              display: "flex",
              alignItems: "center",
              ...(labelFontSizeValue && {
                fontSize: `${labelFontSizeValue}px`,
              }),
            },
          })}>
          {label}
        </InputLabel>
      )}
      <DateConsumer>
        {({ value }) => (
          <InputBase
            id={id}
            sx={{ display: "none" }}
            value={value}
            name={name}
            inputProps={{ "data-testid": dataTestId }}
          />
        )}
      </DateConsumer>
      <Box display="flex">{children}</Box>
      <FormHelperText error {...(error && { sx: { margin: 0 } })}>
        {error}
      </FormHelperText>
    </DateProvider>
  );
}
